import { useTranslation } from 'next-i18next'
import { retrieveErrorCode } from '@frontend/components/shared/ErrorCode'
import { logException } from '@frontend/lib/logger'

import { NOTIFICATION_STATUS } from '@shared/const'

import { useNotify } from './notify'
import { useOptionalUser } from './OptionalUserContext'

type MutateWithNotifParams<T> = {
  successMessage?: string | React.ReactNode | ((data: T) => React.ReactNode)
  errorMessage?: string | React.ReactNode | ((data: Error) => React.ReactNode)
  onError?: (error: Error) => void
  cleanUp?: () => void
}

export const useMutateWithNotifications = () => {
  const notify = useNotify()
  const currentUser = useOptionalUser()
  const { t } = useTranslation('common')

  return async <T>(
    mutateFunc: () => Promise<T>,
    { successMessage, errorMessage, onError, cleanUp }: MutateWithNotifParams<T>
  ) => {
    try {
      const result = await mutateFunc()

      if (successMessage) {
        notify({
          message: typeof successMessage === 'function' ? successMessage(result) : successMessage,
          type: NOTIFICATION_STATUS.success,
        })
      }

      return result
    } catch (err) {
      if (onError) {
        onError(err as Error)
        return
      }

      if (!errorMessage) {
        logException(err as Error, currentUser)
        notify({
          message: retrieveErrorCode({ err, t }),
          type: NOTIFICATION_STATUS.error,
        })
        return
      }

      logException(err as Error, currentUser)
      notify({
        message: typeof errorMessage === 'function' ? errorMessage(err as Error) : errorMessage,
        type: NOTIFICATION_STATUS.error,
      })
    } finally {
      if (cleanUp) {
        cleanUp()
      }
    }
  }
}
